// Storage seam. Implementations: browser localStorage (preview),
// Supabase (current remote), Cloudflare Worker API + D1 + R2 (next).

import type { Note, NoteKind, NoteUpdate } from "../core/types.ts";

export interface NewNote {
  kind?: NoteKind;
  message: string;
  name: string | null;
  paper: string;
  font: string;
  ink: string;
  rotation: number;
  pin: string;
  pos_x: number;
  pos_y: number;
}

/** Someone else changed the board (realtime). */
export interface BoardEvent {
  type: "upsert" | "delete";
  note: Note;
}

export interface MediaChange {
  /** New blob to attach, or null to leave alone. */
  photo: Blob | null;
  audio: Blob | null;
  /** Explicit removal of existing media. */
  removePhoto: boolean;
  removeAudio: boolean;
}

export interface Store {
  readonly remote: boolean;
  /** Returns the authoritative freeze date, if the backend knows one. */
  init(): Promise<Date | null>;
  list(): Promise<Note[]>;
  create(note: NewNote, media: MediaChange): Promise<{ note: Note; token: string | null }>;
  /** Token-gated owner update. Returns the patch actually applied (incl. fresh media URLs). */
  update(
    id: string,
    token: string | null,
    fields: NoteUpdate,
    media: MediaChange
  ): Promise<Partial<Note>>;
  remove(id: string, token: string | null): Promise<void>;
  saveEmail(email: string): Promise<void>;
  /** Live board changes from other people; no-op where unsupported.
   *  onPresence reports how many people have the board open right now. */
  subscribe(onEvent: (e: BoardEvent) => void, onPresence?: (count: number) => void): void;
  /** Fire-and-forget product telemetry (anonymous visitor id + event). */
  track(visitor: string, name: string, props?: Record<string, unknown>): void;
  /** Attach the attendance word mid-session, without a page reload. */
  setRoomWord?(word: string): void;
}

export const NO_MEDIA: MediaChange = {
  photo: null,
  audio: null,
  removePhoto: false,
  removeAudio: false,
};
